import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import userService from 'core/services/user.service';
import { useAuthStore } from 'core/store';
import { ROUTES } from 'core/constants';
import { decodeJWT, isJWTExpired } from 'core/utils/jwt';
import { createAmplitudeEventProperties, logAmplitudeEventWithProps, logAppEntered } from 'core/amplitude/amplitude';
import packageJson from '../../../package.json';

interface UrlAuthParams {
  username?: string;
  password?: string;
  companyId?: string;
  companyName?: string;
  databaseId?: string;
  auth_token?: string;
  auditLogs_Api?: string;
  auditLogsApiEndpoint?: string;
  entity?: string;
  entityId?: string;
}

export default function EmbeddedAutoLogin() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasRedirected, setHasRedirected] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { login } = useAuthStore();

  // Detect if we're running in an embedded context
  const isEmbedded = () => {
    try {
      // Check if we're in an iframe or embedded web view
      return (
        window.self !== window.top ||
        window.location.href.includes('embedded') ||
        navigator.userAgent.includes('Electron') ||
        navigator.userAgent.includes('WebView')
      );
    } catch (e) {
      // If we can't access window.top, we're likely embedded
      return true;
    }
  };

  // Listen for messages from desktop app
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'REDIRECT_TO_LOGIN') {
        console.log(
          'EmbeddedAutoLogin: Received redirect message from desktop app'
        );
        window.location.href = event.data.url || '/';
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Helper function for robust navigation to login page
  const redirectToLogin = () => {
    // eslint-disable-next-line no-debugger
    //debugger;

    if (hasRedirected) {
      console.log(
        'EmbeddedAutoLogin: Already redirected, preventing infinite loop'
      );
      return;
    }

    setHasRedirected(true);
    const embedded = isEmbedded();
    console.log(
      'EmbeddedAutoLogin: Redirecting to login, embedded context:',
      embedded
    );

    // Clear any authentication data to prevent dashboard access
    localStorage.removeItem('auth_token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    //localStorage.removeItem(USER_IDENTITY);
    console.log('EmbeddedAutoLogin: Cleared authentication data');

    if (embedded) {
      // For embedded contexts, use direct navigation immediately
      console.log(
        'EmbeddedAutoLogin: Using direct navigation for embedded context'
      );

      // IMMEDIATE redirect for embedded contexts
      console.log('EmbeddedAutoLogin: Immediate redirect to login page');
      window.location.href = ROUTES.LOGIN;

      // Try multiple navigation methods for embedded contexts
      try {
        window.location.href = ROUTES.LOGIN;
      } catch (e) {
        console.warn(
          'window.location.href failed, trying window.location.replace'
        );
        window.location.replace(ROUTES.LOGIN);
      }

      // Additional immediate fallback for stubborn embedded contexts
      setTimeout(() => {
        if (window.location.pathname !== ROUTES.LOGIN) {
          console.warn(
            'Direct navigation failed, trying window.location.replace'
          );
          try {
            window.location.replace(ROUTES.LOGIN);
          } catch (e) {
            console.warn('window.location.replace failed, trying postMessage');
            // Try to communicate with parent/desktop app
            if (window.parent && window.parent !== window) {
              window.parent.postMessage(
                { type: 'REDIRECT_TO_LOGIN', url: ROUTES.LOGIN },
                '*'
              );
            }
          }
        }
      }, 100);

      // Set up periodic check for navigation success
      const checkInterval = setInterval(() => {
        if (window.location.pathname === ROUTES.LOGIN) {
          console.log('EmbeddedAutoLogin: Navigation to login successful');
          clearInterval(checkInterval);
        } else {
          console.warn(
            'EmbeddedAutoLogin: Navigation still pending, trying again'
          );
          try {
            window.location.href = ROUTES.LOGIN;
          } catch (e) {
            console.warn('Periodic navigation attempt failed');
          }
        }
      }, 500);

      // Clear interval after 10 seconds to prevent infinite attempts
      setTimeout(() => clearInterval(checkInterval), 10000);

      return;
    }

    try {
      // Try React Router navigation first for browser contexts
      console.log('EmbeddedAutoLogin: Using React Router navigation');
      navigate(ROUTES.LOGIN, { replace: true });
    } catch (navError) {
      console.warn('React Router navigation failed, using fallback:', navError);
      // Fallback: force page reload to login
      window.location.href = ROUTES.LOGIN;
    }

    // Additional fallback for embedded contexts
    setTimeout(() => {
      // Check if we're still on the same page (navigation might have failed)
      if (window.location.pathname !== ROUTES.LOGIN) {
        console.warn('Navigation to login failed, forcing page reload');
        window.location.href = ROUTES.LOGIN;
      }
    }, 1000);
  };
  // eslint-disable-next-line no-debugger
  //debugger;
  // Parse URL query parameters
  const parseUrlParams = (): UrlAuthParams => {
    const urlParams = new URLSearchParams(window.location.search);
    const auditLogsApi = urlParams.get('auditLogsApi');

    // Handle case-insensitive parameter names
    const authToken =
      urlParams.get('auth_token') || urlParams.get('auth_Token') || undefined;

    // Parse entity filter parameters from desktop app
    const entity = urlParams.get('entity') || undefined;
    const entityId = urlParams.get('entityId') || undefined;

    return {
      auth_token: authToken,
      auditLogs_Api: auditLogsApi
        ? decodeURIComponent(auditLogsApi)
        : undefined,
      entity,
      entityId,
    };
  };

  useEffect(() => {
    const params = parseUrlParams();

    // Check if we have the minimum required parameters
    if (params.auth_token) {
      //debugger;

      // Check for token in localStorage first (injected by desktop app)
      const localStorageToken = localStorage.getItem('auth_token');

      if (params.auth_token || localStorageToken) {
        // If we have a token (from URL or localStorage), use it directly
        const tokenToUse = params.auth_token || localStorageToken;

        // Decode JWT to extract user information
        const decodedJWT = decodeJWT(tokenToUse!);

        if (decodedJWT) {
          console.log('EmbeddedAutoLogin: JWT decoded successfully');
          console.log('EmbeddedAutoLogin: Decoded JWT properties:', {
            sub: decodedJWT.sub,
            cpid: decodedJWT.cpid,
            dbid: decodedJWT.dbid,
            svcqueueurl: decodedJWT.auditLogsApi,
            exp: decodedJWT.exp,
          });

          // Check if token is expired
          if (isJWTExpired(decodedJWT)) {
            setError('Authentication token has expired. Please log in again.');
            setIsLoading(false);
            setTimeout(() => {
              redirectToLogin();
            }, 3000);
            return;
          }
          // eslint-disable-next-line no-debugger
          //debugger;
          // Set up localStorage with JWT data
          localStorage.setItem('auth_token', tokenToUse!);
          localStorage.setItem(
            'username',
            decodedJWT.sub || params.username || ''
          );
          localStorage.setItem(
            'companyId',
            decodedJWT.cpid || params.companyId || ''
          );
          localStorage.setItem('companyName', decodedJWT.cpname || '');
          localStorage.setItem(
            'databaseId',
            decodedJWT.dbid || params.databaseId || ''
          );

          // Set queue API endpoint from JWT if available
          if (decodedJWT.auditLogsApi) {
            // Normalize the API endpoint URL (remove trailing slash if present)
            let normalizedEndpoint = decodedJWT.auditLogsApi;
            if (normalizedEndpoint.endsWith('/')) {
              normalizedEndpoint = normalizedEndpoint.slice(0, -1);
            }

            localStorage.setItem('auditLogsApi', normalizedEndpoint);
            //dispatch(setauditLogsApiEndpoint(normalizedEndpoint));
            console.log(
              'EmbeddedAutoLogin: Set queue API from JWT:',
              normalizedEndpoint
            );
          } else if (params.auditLogs_Api) {
            // Normalize the API endpoint URL (remove trailing slash if present)
            let normalizedEndpoint = params.auditLogs_Api;
            if (normalizedEndpoint.endsWith('/')) {
              normalizedEndpoint = normalizedEndpoint.slice(0, -1);
            }

            // localStorage.setItem('auditLogsApi', normalizedEndpoint);
            // dispatch(setauditLogsApiEndpoint(normalizedEndpoint));
            console.log(
              'EmbeddedAutoLogin: Set queue API from URL params:',
              normalizedEndpoint
            );
          }

          // Use JWT data to populate missing parameters
          const enhancedParams: UrlAuthParams = {
            ...params,
            auth_token: tokenToUse || undefined,
            username: decodedJWT.sub || params.username,
            companyId: decodedJWT.cpid || params.companyId,
            companyName: decodedJWT.cpname || undefined,
            databaseId: decodedJWT.dbid || params.databaseId,
            auditLogs_Api: decodedJWT.auditLogsApi || params.auditLogs_Api,
          };

          console.log(
            'EmbeddedAutoLogin: Using enhanced parameters from JWT:',
            {
              username: enhancedParams.username,
              companyId: enhancedParams.companyId,
              databaseId: enhancedParams.databaseId,
              auditLogsApiEndpoint: enhancedParams.auditLogs_Api,
              tokenExpiry: decodedJWT.exp
                ? new Date(decodedJWT.exp * 1000).toLocaleString()
                : 'unknown',
            }
          );

          performTokenBasedAuth(enhancedParams);
        } else {
          // Fallback to original parameters if JWT decoding fails
          const authParams: UrlAuthParams = {
            ...params,
            auth_token: tokenToUse || undefined,
          };
          performTokenBasedAuth(authParams);
        }
      } else {
        setError(
          'Missing required parameters. Need either auth_token (in URL or localStorage) or password.'
        );
        setIsLoading(false);
      }
    } else {
      setError(
        'Missing required parameters: username, companyId, and databaseId are required.'
      );
      setIsLoading(false);
    }
  }, []);

  // Handle error state and redirect logic
  useEffect(() => {
    if (error && !hasRedirected) {
      console.log(
        'EmbeddedAutoLogin: Error state detected, forcing redirect to login'
      );
      setTimeout(() => {
        redirectToLogin();
      }, 100);
    }
  }, [error, hasRedirected]);
  
  // Setup user and cache after successful authentication
  async function setupUserAndCache(userInfo: any, params: UrlAuthParams) {
    try {
      console.log('EmbeddedAutoLogin: Setting up user and cache');

      // Set authentication state in the store
      const token = localStorage.getItem('auth_token');

      if (token && userInfo) {
        userInfo.companyName = localStorage.getItem('companyName');
        userInfo.userNameAndCompany = `${userInfo.userName}@${userInfo.companyName}`;
        login(userInfo, token);
        console.log('EmbeddedAutoLogin: User authenticated successfully');

        // Track app entry for analytics (desktop/embedded auto-login)
        logAppEntered(
          userInfo.userNameAndCompany,
          userInfo.company,
          'desktop',
          packageJson.version
        );

        logAmplitudeEventWithProps(
          userInfo.userNameAndCompany,
          {
            company: localStorage.getItem("companyName") || "",
            user: userInfo.userName || "",
          },
          "AuditLogEmbeddedLogin",
          createAmplitudeEventProperties(
            "AuditLogEmbeddedLogin",
            "AuditLogEmbeddedAutoLogin.tsx",
            packageJson.version,
            "0",
          ),
        );

        // Set additional user data in localStorage
        localStorage.setItem('isAuthenticated', 'true');
        // eslint-disable-next-line no-debugger
        //debugger;
        // Store entity filter parameters for the audit page to use
        if (params.entity) {
          localStorage.setItem('auditEntityType', params.entity);
          console.log(
            'EmbeddedAutoLogin: Set entity type filter:',
            params.entity
          );
        } else {
          localStorage.removeItem('auditEntityType');
        }
        if (params.entityId) {
          localStorage.setItem('auditEntityId', params.entityId);
          console.log(
            'EmbeddedAutoLogin: Set entity ID filter:',
            params.entityId
          );
        } else {
          localStorage.removeItem('auditEntityId');
        }

        // Navigate to the audit page (main landing page)
        console.log('EmbeddedAutoLogin: Navigating to audit page');
        navigate(ROUTES.AUDIT, { replace: true });
      } else {
        throw new Error('Missing token or user information');
      }
    } catch (error) {
      console.error(
        'EmbeddedAutoLogin: Error setting up user and cache:',
        error
      );
      setError('Failed to set up user session. Please try again.');
      setTimeout(() => {
        redirectToLogin();
      }, 2000);
    }
  }

  async function performTokenBasedAuth(params: UrlAuthParams) {
    try {
      //debugger;
      setIsLoading(true);
      setError(null);

      console.log('EmbeddedAutoLogin: Using token-based authentication');
      console.log('EmbeddedAutoLogin: Parameters:', {
        username: params.username,
        companyId: params.companyId,
        databaseId: params.databaseId,
        auth_token: params.auth_token ? '***' : 'undefined',
        auditLogs_Api: params.auditLogs_Api,
      });

      // Authentication token should already be set in localStorage from JWT decoding
      // Just verify it's there
      const storedauth_token = localStorage.getItem('auth_token');
      const storedUsername = localStorage.getItem('username');
      const auditLogsApi = localStorage.getItem('auditLogsApi');

      console.log('EmbeddedAutoLogin: Stored values from localStorage:', {
        username: storedUsername,
        auditLogsApi,
        auth_token: storedauth_token ? '***' : 'undefined',
      });

      if (!storedauth_token) {
        throw new Error('No authentication token found in localStorage');
      }

      if (!storedUsername) {
        throw new Error('No username found in localStorage');
      }

      // Small delay to ensure localStorage is properly set
      await new Promise(resolve => setTimeout(resolve, 100));

      // Get user information using the token
      console.log(
        'EmbeddedAutoLogin: Calling getUserByUserName for:',
        storedUsername
      );
      const userInfo = await userService.getUserByUserName(storedUsername);

      if (userInfo) {
        console.log('EmbeddedAutoLogin: User info retrieved successfully');
        await setupUserAndCache(userInfo, params);
      } else {
        const errorMsg = 'Failed to fetch user information';
        console.error('EmbeddedAutoLogin:', errorMsg);
        setError(errorMsg);
        // Redirect to login page after showing error
        const timeout = isEmbedded() ? 1000 : 3000; // Faster redirect for embedded contexts
        setTimeout(() => {
          redirectToLogin();
        }, timeout);
      }
    } catch (err: any) {
      // console.error('EmbeddedAutoLogin: Token-based auth error:', err);
      // console.error('EmbeddedAutoLogin: Error details:', {
      //   message: err.message,
      //   status: err.response?.status,
      //   statusText: err.response?.statusText,
      //   url: err.config?.url,
      //   baseURL: err.config?.baseURL,
      // });

      let errorMsg = 'Failed to authenticate with token. Please try again.';

      if (err.response?.status === 404) {
        errorMsg =
          'API endpoint not found. Please check the auditLogsApiEndpoint parameter.';
      } else if (err.code === 'ERR_NETWORK') {
        errorMsg =
          'Network error. Please check your connection and API endpoint.';
      } else if (err.response?.status === 401) {
        errorMsg =
          'Invalid authentication token. Please check your credentials.';
      }

      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }

  // This logic should not run on every render - it's handled in useEffect

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Setting up your session...
          </h2>
          <p className="text-gray-500">
            Please wait while we configure your application.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Setup Failed
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors mr-3"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate(ROUTES.LOGIN)}
            className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Go to Login
          </button>
          {isEmbedded() && (
            <button
              onClick={() => {
                console.log(
                  'EmbeddedAutoLogin: Force redirect to login for embedded context'
                );
                window.location.href = ROUTES.LOGIN;
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors ml-2"
            >
              Force Redirect
            </button>
          )}
        </div>
      </div>
    );
  }

  return null;
}
